// PlannerChatSection.tsx
// New section rendered below the Upcoming Raids list on PlannerPage.
// Uses the SAME backend endpoint as RaidChatSidebar (/raid-chat/my-raids)
// and re-uses the exported RaidChatPanel for the expanded view.
import React, { useState, useEffect, useCallback } from "react";
import { RaidChatPanel, RaidChat } from "./RaidChatSidebar";

const BACKEND_URL = "";
const PREVIEW_COUNT = 3;

function getGuildLogoUrl(logoPath: string | null): string | null {
  if (!logoPath) return null;
  if (logoPath.startsWith("http")) return logoPath;
  return `${BACKEND_URL}${logoPath}`;
}

function formatSessionLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const isTomorrow =
    d.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (isToday) return `Today · ${time}`;
  if (isTomorrow) return `Tomorrow · ${time}`;
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + ` · ${time}`;
}

export default function PlannerChatSection() {
  const [raids, setRaids] = useState<RaidChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [openRaidId, setOpenRaidId] = useState<string | null>(null);

  const fetchRaids = useCallback(() => {
    fetch(`${BACKEND_URL}/raid-chat/my-raids`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setRaids(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load raid chats:", err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchRaids();
  }, [fetchRaids]);

  if (loading) return null;

  const displayed = showAll ? raids : raids.slice(0, PREVIEW_COUNT);
  const openRaid = openRaidId ? raids.find((r) => r.raid_id === openRaidId) : null;

  return (
    <section className="space-y-3 pt-8 mt-8 border-t border-[var(--c-line-soft)]">
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2">
          <span className="w-1 h-4 bg-purple-500 rounded-sm shadow-[0_0_8px_rgba(168,85,247,0.6)]"></span>
          <h3 className="text-sm font-bold text-[var(--c-ink)] uppercase tracking-widest">
            Raid Chats
          </h3>
          {raids.length > 0 && (
            <span className="text-[10px] text-[var(--c-ink-3)] font-semibold">
              {raids.length} active
            </span>
          )}
        </div>
        {raids.length > PREVIEW_COUNT && (
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-cyan-700 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-semibold transition-colors"
          >
            {showAll ? "Show less" : `Show all (${raids.length})`}
          </button>
        )}
      </div>

      {raids.length === 0 ? (
        <div className="relative overflow-hidden rounded-2xl border border-dashed border-[var(--c-line)] bg-gradient-to-br from-cyan-500/5 via-purple-500/5 to-transparent p-10 text-center">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.08),transparent_50%)] pointer-events-none" />
          <div className="relative z-10 flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center ring-1 ring-[var(--c-line)]">
              <svg
                className="w-8 h-8 text-cyan-700/80 dark:text-cyan-300/80"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <div>
              <div className="text-[var(--c-ink)] font-bold text-base mb-1">
                Start chatting with your raid team
              </div>
              <div className="text-xs text-[var(--c-ink-3)] max-w-sm mx-auto leading-relaxed">
                Once you're signed up for a raid, your raid chat will appear here.
                Coordinate strategy, share logs, and keep the banter going between
                pulls.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {displayed.map((raid) => {
            const logoUrl = getGuildLogoUrl(raid.guild_logo_url);
            const timeLabel = formatSessionLabel(raid.next_session_time);
            return (
              <button
                key={raid.raid_id}
                onClick={() => setOpenRaidId(raid.raid_id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] hover:bg-[var(--c-sunken)] hover:border-cyan-500/30 transition-all group text-left"
              >
                <div className="relative w-10 h-10 rounded-full overflow-hidden ring-1 ring-[var(--c-line)] flex-shrink-0 bg-[var(--c-sunken)]">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-[var(--c-ink-2)] bg-gradient-to-br from-[var(--c-sunken-2)] to-[var(--c-sunken)]">
                      {raid.guild_tag ||
                        (raid.guild_name || raid.display_name || "??")
                          .substring(0, 2)
                          .toUpperCase()}
                    </div>
                  )}
                  {raid.unread_count > 0 && (
                    <div className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-red-500 to-red-600 ring-2 ring-[var(--c-surface)] flex items-center justify-center shadow-lg shadow-red-500/30">
                      <span className="text-[9px] font-bold text-[var(--c-ink)]">
                        {raid.unread_count > 99 ? "99+" : raid.unread_count}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-[var(--c-ink)] truncate">
                      {raid.raid_name}
                    </span>
                    {raid.guild_tag && (
                      <span className="text-[10px] text-cyan-700/70 dark:text-cyan-300/70 flex-shrink-0">
                        &lt;{raid.guild_tag}&gt;
                      </span>
                    )}
                    {raid.is_raid_leader && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-700 dark:text-amber-300 text-[9px] font-bold flex-shrink-0">
                        RL
                      </span>
                    )}
                    {raid.is_loot_master && (
                      <span className="px-1.5 py-0.5 rounded bg-purple-500/20 border border-purple-500/30 text-purple-700 dark:text-purple-300 text-[9px] font-bold flex-shrink-0">
                        LM
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--c-ink-3)] truncate mt-0.5">
                    <span className="text-[var(--c-ink-2)]">{timeLabel}</span>
                    <span className="text-[var(--c-ink-4)]"> · </span>
                    <span className="text-cyan-700 dark:text-cyan-400">{raid.character_name}</span>
                    <span className="text-[var(--c-ink-4)]"> · </span>
                    <span className="text-[var(--c-ink-3)]">{raid.character_role}</span>
                  </div>
                </div>

                <svg
                  className="w-4 h-4 text-[var(--c-ink-4)] group-hover:text-cyan-700 dark:group-hover:text-cyan-400 group-hover:translate-x-0.5 transition-all flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            );
          })}
        </div>
      )}

      {openRaid && (
        <RaidChatPanel
          raidId={openRaid.raid_id}
          raid={openRaid}
          onClose={() => {
            setOpenRaidId(null);
            fetchRaids(); // refresh unread counts on close — same pattern as sidebar
          }}
        />
      )}
    </section>
  );
}